import { z } from "zod";
import {
  ColorOptionLabel,
  MiDetailFields,
  MiModal,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CONFIGURABLE_COLORS,
  ENTITY_NAMES,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { capitalize, createUUID } from "@miplatform/utils";

const initialOrderData = {
  name: "",
  shortName: "",
  color: "",
  tradeId: "",
  requiresApproval: false,
  refrenceId: createUUID(),
};

export function WorkOrderTemplateModal({
  open,
  title,
  buttonTitle,
  onClose,
  trades,
  loading,
  onSubmit = () => null,
}) {
  const workOrderTemplateFields = [
    {
      label: `${ENTITY_NAMES.WORK_ORDER_TEMPLATE} Name`,
      name: "name",
      autoFocus: true,
      beforeOnChange: capitalize,
      width: "100%",
      validationRule: z
        .string()
        .min(1, `${ENTITY_NAMES.WORK_ORDER_TEMPLATE} Name is required`),
      required: true,
    },
    {
      label: "Trade",
      name: "tradeId",
      fieldType: INPUT_TYPES.SELECT,
      options: trades,
      width: "100%",
      validationRule: z.string().min(1, "Trade Name is required"),
      required: true,
    },
    {
      label: "Abbr.",
      name: "shortName",
      beforeOnChange: (value) => value.toUpperCase(),
      width: "100%",
      validationRule: z.string().min(1, "Abbreviation is required"),
      required: true,
    },
    {
      label: "Color",
      name: "color",
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      fullWidth: true,
      width: "100%",
      validationRule: z.string().min(1, "Color is required"),
      required: true,
    },
  ];

  const workOrderTemplateEdit = useEdit(
    initialOrderData,
    workOrderTemplateFields
  );

  const handleModalClose = () => {
    onClose();
    workOrderTemplateEdit.reset();
  };

  const handleSubmit = () => {
    onSubmit(workOrderTemplateEdit.edits);
  };

  return (
    <MiModal
      open={open}
      closeModal={handleModalClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleModalClose,
        },
        {
          name: buttonTitle,
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: handleSubmit,
          disabled: loading || !workOrderTemplateEdit.isValid(),
        },
      ]}
      title={title}
      topRightClose={workOrderTemplateEdit.reset}
      loading={loading}
    >
      <MiDetailFields
        detailFields={workOrderTemplateFields}
        edit={workOrderTemplateEdit}
      />
    </MiModal>
  );
}
